import { useId, useRef, useState } from 'react';
import { toast } from 'sonner';
import { LockIcon } from 'lucide-react';
import { Panel } from '../ui/Panel';
import { NumberInput } from '../ui/NumberInput';
import { AutoTextarea } from '../ui/AutoTextarea';
import { useSubmit } from '../../hooks/useSubmit';
import { closeKassaDay } from '../adminApi';
import { newRequestId } from '../../core/api/http';
import { closeProblem } from '../../core/rules/kassa';
import { btnPrimary, inputCls } from '../ui/styles';
import { money } from '../../core/format/currency';
import { clock, refundMethodLabel } from './kassaLabels';
import { printKuurCreditNote } from './kuurCreditNote';
import { printReturnCreditNote } from './returnCreditNote';
import { useAdmin } from '../AdminContext';
import { closedByLabel } from '../../core/rules/staffAccess';
import type { KassaDay, KuurStop, SaleReturn } from '../../core/types/kassa';
type Props = {
  data: KassaDay;
  onClosed: (day: KassaDay) => void;
  /** Collega: geen dagtotalen en blind tellen (core/rules/staffAccess). */
  totalsHidden?: boolean;
};

/** Het dagoverzicht: wat er binnenkwam per methode, en de afsluiting met de telling van de kas. */
export function DaySummary({
  data,
  onClosed,
  totalsHidden = false
}: Props) {
  const s = data.summary;
  const canClose = !data.closed;
  const count = s.sales === 0 ? 'Nog geen bonnen.' : `${s.sales} ${s.sales === 1 ? 'bon' : 'bonnen'}${s.corrected ? ` · ${s.corrected} gecorrigeerd` : ''}`;
  if (totalsHidden) {
    return <Panel title="Dag afsluiten" description={`Jouw bonnen vandaag: ${count.toLowerCase()}`} data-id="element-2226" data-mp="39p1oafewynlc">
        {data.closed ? <p className="flex items-center gap-2 text-[13px] text-ink" data-id="element-2227" data-mp="3not7qvo78w6q">
            <LockIcon className="h-4 w-4 text-muted" strokeWidth={1.75} aria-hidden="true" data-id="element-2228" data-mp="3jn7psvwt5rpo" />
            Afgesloten{closedByLabel(data.closed)} om {clock(data.closed.closedAt)} · geteld {money(data.closed.countedCashCents)}
          </p> : canClose && <CloseForm data={data} onClosed={onClosed} blind data-id="element-2229" data-mp="ywh1ujynfthw" />}
      </Panel>;
  }
  return <Panel title="Dagoverzicht" description={count} data-id="element-2230" data-mp="3rqyzn491ak28">
      <p className="font-display text-[2.2rem] font-light leading-none tabular-nums text-ink" data-id="element-2231" data-mp="k4o2cgpdx6w">{money(s.totalCents)}</p>
      <p className="mt-1 text-[12px] text-muted" data-id="element-2232" data-mp="5lbw8cmrcye3">Totaal incl. btw</p>

      <dl className="mt-5 space-y-1.5 border-t border-line pt-4 text-[14px]" data-id="element-2233" data-mp="9m45v6zjl5aw">
        <Row label="Pin" value={s.byMethod.pin} data-id="element-2234" data-mp="390psrh2ay447" />
        <Row label="Contant" value={s.byMethod.contant} data-id="element-2235" data-mp="3920rv3ksynoz" />
        <Row label="Cadeaubon" value={s.byMethod.cadeaubon} data-id="element-2236" data-mp="3dzkil2yossxt" />
        {(s.byMethod.tegoed ?? 0) > 0 && <Row label="Klanttegoed" value={s.byMethod.tegoed} data-id="element-2237" data-mp="3i1nwrzcqqbms" />}
      </dl>
      {s.voidRefunds && s.voidRefunds.pin + s.voidRefunds.contant > 0 && <p className="mt-3 text-[12px] text-muted" data-id="element-2238" data-mp="3mtp4r92vlg70">
          Teruggegeven bij gecorrigeerde bonnen:{' '}
          {[s.voidRefunds.pin > 0 ? `${money(s.voidRefunds.pin)} pin` : '', s.voidRefunds.contant > 0 ? `${money(s.voidRefunds.contant)} contant` : ''].filter(Boolean).join(' · ')}. Die bonnen
          tellen hierboven al niet mee; op de pinautomaat staat de terugboeking wel.
        </p>}

      {(data.kuurRefunds?.length ?? 0) > 0 && <KuurRefunds refunds={data.kuurRefunds ?? []} data-id="element-2239" data-mp="2drm4l0px26s9" />}
      {(data.returns?.length ?? 0) > 0 && <Returns returns={data.returns ?? []} data-id="element-2240" data-mp="2dtgoqctd5uva" />}

      <dl className="mt-4 space-y-1.5 border-t border-line pt-4 text-[13px]" data-id="element-2241" data-mp="28rhpnf4npk45">
        <Row label="Behandelingen" value={s.byKind.treatments} small data-id="element-2242" data-mp="23tzpoe8maf8a" />
        <Row label="Producten" value={s.byKind.products} small data-id="element-2243" data-mp="23vaodzn7gwsj" />
        <Row label="Kuren vooruit" value={s.byKind.kuren} small data-id="element-2244" data-mp="1z16kxqd5f6w7" />
        {(s.byKind.giftCards ?? 0) > 0 && <Row label="Cadeaubonnen verkocht (geen omzet)" value={s.byKind.giftCards} small data-id="element-2245" data-mp="1u46a725ctfyr" />}
        {s.vat.map(v => <Row key={v.vatRate} label={`Waarvan btw ${v.vatRate}%`} value={v.vatCents} small data-id="element-2246" data-mp="1pzyoldu70g4r" />)}
      </dl>

      <div className="mt-5 border-t border-line pt-4" data-id="element-2247" data-mp="1g3vjbz41o1dl">
        {data.closed ? <div className="space-y-1 text-[13px]" data-id="element-2248" data-mp="1g5yhx7j9j2tx">
            <p className="flex items-center gap-2 text-ink" data-id="element-2249" data-mp="3g767i03al6ih">
              <LockIcon className="h-4 w-4 text-muted" strokeWidth={1.75} aria-hidden="true" data-id="element-2250" data-mp="3l580bcqisk75" />
              Afgesloten{closedByLabel(data.closed)} om {clock(data.closed.closedAt)}
            </p>
            <p className="text-muted" data-id="element-2251" data-mp="3bfeqag610d50">
              Contant verwacht {money(data.closed.expectedCashCents)} · geteld {money(data.closed.countedCashCents)}
              {data.closed.differenceCents !== 0 && ` · ${data.closed.differenceCents > 0 ? 'over' : 'tekort'} ${money(Math.abs(data.closed.differenceCents))}`}
            </p>
            {data.closed.note && <p className="text-ink" data-id="element-2252" data-mp="2nir46atiwcmz">“{data.closed.note}”</p>}
          </div> : canClose && <CloseForm data={data} onClosed={onClosed} data-id="element-2253" data-mp="2shbyxuq0a441" />}
      </div>
    </Panel>;
}

/**
 * Kuren die vandaag met pin of contant zijn terugbetaald (vanuit het klantdossier). Ze zitten al
 * verrekend in Pin en Contant hierboven, en dus in wat er in de la hoort.
 */
function KuurRefunds({
  refunds
}: {
  refunds: KuurStop[];
}) {
  const {
    site
  } = useAdmin();
  const print = async (k: KuurStop) => {
    if (!(await printKuurCreditNote(k, site.salon))) toast.error('Creditnota openen lukte niet', {
      description: 'Probeer het opnieuw of gebruik een andere browser.'
    });
  };
  return <div className="mt-4 border-t border-line pt-4" data-id="element-2254" data-mp="2g59qnu8pprat">
      <p className="text-[13px] text-ink" data-id="element-2255" data-mp="2txpn2c73yr2r">Kuur terugbetaald</p>
      <p className="text-[12px] text-muted" data-id="element-2256" data-mp="2tw4wsyrttuap">Zit al in Pin en Contant hierboven.</p>
      <ul className="mt-2 space-y-1.5 text-[13px]" data-id="element-2257" data-mp="2oxtj2cyeuzn7">
        {refunds.map(k => <li key={k.id} className="flex items-baseline justify-between gap-3" data-id="element-2258" data-mp="33w5dse283ybq">
            <span className="min-w-0" data-id="element-2259" data-mp="2yw9c4lh00izc">
              <span className="block truncate text-ink" data-id="element-2260" data-mp="2yxc28r6hyhe7">{k.customerName}</span>
              <span className="block text-[12px] text-muted" data-id="element-2261" data-mp="19iw34ixxy6rw">
                {clock(k.at)} · {refundMethodLabel[k.method]} ·{' '}
                <button type="button" onClick={() => void print(k)} className="underline underline-offset-2 hover:text-ink" data-id="element-2262" data-mp="14km602snow3n">
                  {k.number}
                </button>
              </span>
            </span>
            <span className="shrink-0 tabular-nums text-ink" data-id="element-2263" data-mp="1yabt3c4net7o">− {money(k.refundCents)}</span>
          </li>)}
      </ul>
    </div>;
}

/** Retouren die vandaag met pin of contant zijn terugbetaald (de bon kan van een eerdere dag zijn). */
function Returns({
  returns
}: {
  returns: SaleReturn[];
}) {
  const {
    site
  } = useAdmin();
  const print = async (r: SaleReturn) => {
    if (!(await printReturnCreditNote(r, site.salon))) toast.error('Creditnota openen lukte niet', {
      description: 'Probeer het opnieuw of gebruik een andere browser.'
    });
  };
  return <div className="mt-4 border-t border-line pt-4" data-id="element-2264" data-mp="2qvuyhd2kkyeq">
      <p className="text-[13px] text-ink" data-id="element-2265" data-mp="2vwixakx1ijji">Retour</p>
      <p className="text-[12px] text-muted" data-id="element-2266" data-mp="2vxu2mp64vzcq">Zit al in Pin en Contant hierboven.</p>
      <ul className="mt-2 space-y-1.5 text-[13px]" data-id="element-2267" data-mp="30wezcem6g0wf">
        {returns.map(r => <li key={r.id} className="flex items-baseline justify-between gap-3" data-id="element-2268" data-mp="p8a6f0kiwnny">
            <span className="min-w-0" data-id="element-2269" data-mp="k970ldwpydex">
              <span className="block truncate text-ink" data-id="element-2270" data-mp="k84dlh2iov4a">{r.customerName}</span>
              <span className="block text-[12px] text-muted" data-id="element-2271" data-mp="f917ruepqkv9">
                {clock(r.at)} · {refundMethodLabel[r.method]} · bon {r.saleNumber} ·{' '}
                <button type="button" onClick={() => void print(r)} className="underline underline-offset-2 hover:text-ink" data-id="element-2272" data-mp="ad40edlfgoum">
                  {r.number}
                </button>
              </span>
            </span>
            <span className="shrink-0 tabular-nums text-ink" data-id="element-2273" data-mp="y8orpzxhm2rg">− {money(r.refundCents)}</span>
          </li>)}
      </ul>
    </div>;
}
function Row({
  label,
  value,
  small = false
}: {
  label: string;
  value: number;
  small?: boolean;
}) {
  return <div className="flex justify-between gap-3" data-id="element-2274" data-mp="1qqkfo038nc05">
      <dt className="text-muted" data-id="element-2275" data-mp="1lyj7ap96y5fe">{label}</dt>
      <dd className={`tabular-nums ${small ? 'text-muted' : 'text-ink'}`} data-id="element-2276" data-mp="1lxhlbpqmzyiw">{money(value)}</dd>
    </div>;
}

/**
 * Tel de la, vul het in; bij een verschil schrijf je kort op wat er gebeurde. Daarna kunnen er geen
 * bonnen meer bij. `blind` (collega): zonder het verwachte bedrag; de server rekent het verschil en
 * Sarah ziet het in haar dagoverzicht.
 */
function CloseForm({
  data,
  onClosed,
  blind = false
}: Omit<Props, 'totalsHidden'> & {
  blind?: boolean;
}) {
  const id = useId();
  const expected = data.summary.expectedCashCents;
  const [counted, setCounted] = useState<number | undefined>(undefined);
  const [note, setNote] = useState('');
  const [touched, setTouched] = useState(false);
  const {
    submit,
    submitting,
    error
  } = useSubmit(closeKassaDay);
  const request = useRef(newRequestId());
  const problem = blind ? counted === undefined ? 'Vul in wat er in de la zit.' : null : closeProblem(counted, expected, note);
  const difference = blind || counted === undefined ? null : counted - expected;
  const close = async () => {
    setTouched(true);
    if (problem || counted === undefined) return;
    const day = await submit(data.day, {
      requestId: request.current,
      countedCashCents: counted,
      note: note.trim()
    });
    if (!day) return;
    toast.success('Dag afgesloten', {
      description: 'Deze dag neemt geen bonnen meer aan.'
    });
    onClosed(day);
  };
  return <div className="space-y-3" data-id="element-2277" data-mp="3q7nr9wbufxv8">
      <p className="text-[13px] text-ink" data-id="element-2278" data-mp="3q8p2c0sv0kcy">
        Dag afsluiten {blind ? <span className="text-muted" data-id="element-2279" data-mp="32hjkxh79tk0j">· tel wat er contant in de la zit</span> : <span className="text-muted" data-id="element-2280" data-mp="2ygzqwmhzuwim">· contant verwacht {money(expected)}</span>}
      </p>
      <label className="block" data-id="element-2281" data-mp="3c84id1b7kihv">
        <span className="mb-1 block text-[12px] text-muted" data-id="element-2282" data-mp="3c8e8cow96pnk">Geteld in de la (€, zonder wisselgeldvoorraad)</span>
        <NumberInput decimals allowEmpty value={counted === undefined ? undefined : counted / 100} onChange={v => setCounted(v === undefined ? undefined : Math.round(v * 100))} className="w-36" data-id="element-2283" data-mp="37gmhsed3kjxf" />
      </label>
      {difference !== null && difference !== 0 && <p className="text-[13px] text-clay" aria-live="polite" data-id="element-2284" data-mp="ms8paqf6nily">
          {difference > 0 ? 'Over' : 'Tekort'}: {money(Math.abs(difference))}
        </p>}
      <label htmlFor={`${id}-note`} className="block" data-id="element-2285" data-mp="hu7aihosi5ga">
        <span className="mb-1 block text-[12px] text-muted" data-id="element-2286" data-mp="ctjhxrkwxgjy">{blind ? 'Notitie voor Sarah (optioneel)' : `Notitie ${difference ? '(verplicht bij een verschil)' : '(optioneel)'}`}</span>
        <AutoTextarea id={`${id}-note`} value={note} onChange={e => setNote(e.target.value)} minRows={2} maxLength={300} className={inputCls} data-id="element-2287" data-mp="3u4t0wke9e1k" />
      </label>
      {(touched && problem || error) && <p role="alert" className="text-[12px] text-clay" data-id="element-2288" data-mp="3q8o7suda6v8k">
          {error ?? problem}
        </p>}
      <button type="button" onClick={() => void close()} disabled={submitting} aria-busy={submitting} className={`${btnPrimary} w-full`} data-id="element-2289" data-mp="3law5d6f0fqq4">
        {submitting ? 'Afsluiten…' : 'Dag afsluiten'}
      </button>
    </div>;
}